import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { IAddMedicationAdministeredState } from '../../addMedicationAdministered/store/addMedicationAdministered.types'
import { IOptions } from '../../../common/common.types'
import { IMedicationOptions } from '../../patientMedications/store/patientMedications.types'

interface CreateMedicationProcedureProps {
  procedureEvent: IEKGEvent
  medicationsOptions: IMedicationOptions
  doseUnitOptions: IOptions
  ptaCutoff: Date | null
}

export interface MedicationProcedureObject {
  timelineItem: IItem
  addMedicationAdministeredState: IAddMedicationAdministeredState
}

const createMedicationProcedure = (props: CreateMedicationProcedureProps): MedicationProcedureObject | null => {
  const { procedureEvent, medicationsOptions, doseUnitOptions, ptaCutoff } = props

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  if (!procedureEvent.procedureAction) return null

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const medicationId = Number(procedureEvent.procedureAction)
  const doseUnitId = Number(procedureEvent.procedureDetail3)

  const medicationObject = medicationsOptions.find((option) => option.id === medicationId)
  const medicationName = medicationObject?.name || ''

  const doseUnitName = doseUnitOptions.find((option) => option.id === doseUnitId)?.name
  const doseUnitPNNVOption = doseUnitOptions.find((option) => option.id === doseUnitId && (option.isNV || option.isPN))
  let nvValue, pnValue
  if (doseUnitName) {
    nvValue = doseUnitPNNVOption?.isNV ? String(doseUnitPNNVOption?.nemsisCode || '') : ''
    pnValue = doseUnitPNNVOption?.isPN ? String(doseUnitPNNVOption?.nemsisCode || '') : ''
  }

  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `${medicationName || ''} By , null ${doseUnitName || ''}, Via , Comments: ${itemComments}.`
  const clientCorrelationId = `patient.medicationAdministeredStateList_${clientEventId}`
  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Medications',
    event: 'Medication Administered',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_ADD_MEDICATION_ADMINISTERED_ITEM,
    defaultHref: TabConstants.ADD_MEDICATIONS_ADMINISTERED,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  const addMedicationAdministeredState: IAddMedicationAdministeredState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    pta: isPTA,
    dateTime: '',
    assessedBy: null,
    rolePerforming: null,
    rolePerformingPNNV: null,
    administeredByAnotherUnit: null,
    administeredByAnotherUnitPNNV: null,
    medication: medicationObject || null,
    dose: null,
    doseUnit: doseUnitName ? { id: Number(procedureEvent.procedureDetail3), name: doseUnitName } : null,
    doseUnitPNNV: {
      id: Number(procedureEvent.procedureDetail3),
      name: doseUnitName || '',
      nv: nvValue || '',
      pn: pnValue || '',
    },
    rate: null,
    rateUnit: null,
    frequency: null,
    route: null,
    routePNNV: null,
    concentration: '',
    response: null,
    responsePNNV: null,
    complications: [],
    authorization: null,
    wasted: false,
    lotNumber: '',
    witness1: '',
    witness2: '',
    isEkgImport: 1,
    nemsisCode: null,
    medicationAdministered: {
      key: clientEventId,
      medication: medicationObject || null,
      dose: null,
      doseUnit: doseUnitName ? { id: Number(procedureEvent.procedureDetail3), name: doseUnitName } : null,
      doseUnitPNNV: {
        id: Number(procedureEvent.procedureDetail3),
        name: doseUnitName || '',
        nv: nvValue || '',
        pn: pnValue || '',
      },
      rate: null,
      rateUnit: null,
      frequency: null,
      route: null,
      routePNNV: null,
      concentration: '',
      response: null,
      responsePNNV: null,
      complications: [],
      wasted: false,
      lotNumber: '',
      witness1: '',
      witness2: '',
    },
    comments: procedureEvent.procedureComments || '',
    addMedicationAdministeredData: [
      {
        key: clientEventId,
        medication: medicationObject || null,
        dose: null,
        doseUnit: doseUnitName ? { id: Number(procedureEvent.procedureDetail3), name: doseUnitName } : null,
        doseUnitPNNV: {
          id: Number(procedureEvent.procedureDetail3),
          name: doseUnitName || '',
          nv: nvValue || '',
          pn: pnValue || '',
        },
        rate: null,
        rateUnit: null,
        frequency: null,
        route: null,
        routePNNV: null,
        concentration: '',
        response: null,
        responsePNNV: null,
        complications: [],
        wasted: false,
        lotNumber: '',
        witness1: '',
        witness2: '',
      },
    ],
    areMedicationsOptionsLoading: false,
    addToTimeline: true,
    type: 'Medication',
    currentMedication: {
      key: clientEventId,
      medication: medicationObject || null,
      dose: null,
      doseUnit: doseUnitName ? { id: Number(procedureEvent.procedureDetail3), name: doseUnitName } : null,
      doseUnitPNNV: {
        id: Number(procedureEvent.procedureDetail3),
        name: doseUnitName || '',
        nv: nvValue || '',
        pn: pnValue || '',
      },
      rate: null,
      rateUnit: null,
      frequency: null,
      route: null,
      routePNNV: null,
      concentration: '',
      response: null,
      responsePNNV: null,
      complications: [],
      wasted: false,
      lotNumber: '',
      witness1: '',
      witness2: '',
    },
    medicationDefaults: null,
  }

  const medicationProcedureObject: MedicationProcedureObject = {
    addMedicationAdministeredState,
    timelineItem,
  }

  return medicationProcedureObject
}

export default createMedicationProcedure
